import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Section, SectionDataRecord } from 'share/domain/case'
import { GetCaseQuestionnaireSectionResponse } from 'share/queries/cases'
import {
  saveActiveSectionToSessionStorage,
  saveSectionUiTransformedModelToSessionStorage,
  saveSectionCurrentActiveQuestionId,
  saveSectionCurrentActiveGroup
} from './activesectionSessionStorage'

export interface ActiveQuestionnaireSection {
  isActiveSection: boolean
  activeQuestionId: number
  section: Section | null
}

const initialState: ActiveQuestionnaireSection = {
  isActiveSection: false,
  activeQuestionId: 0,
  section: null
}

export function getInactiveActiveQuestionnaireSection(): ActiveQuestionnaireSection {
  const data: ActiveQuestionnaireSection = {
    isActiveSection: false,
    activeQuestionId: 0,
    section: null
  }
  return data
}

export function getEmptySectionDataRecord(): SectionDataRecord {
  const data: SectionDataRecord = {
    id: 0,
    sectionId: 0,
    questionKey: '',
    questionDataJsonText: '',
    questionCompleteFlag: false
  }

  return data
}

export function getEmptyActiveQuestionnaireSection(): ActiveQuestionnaireSection {
  const data: ActiveQuestionnaireSection = {
    isActiveSection: true,
    activeQuestionId: 0,
    section: {
      id: 0,
      sectionSystemName: '',
      sectionName: '',
      sectionStatus: 0,
      sectionDescription: '',
      sectionOrder: 0,
      version: 0,
      comments: null,
      currentActiveQuestionId: null,
      currentActiveGroup: null,
      isRejected: false,
      sectionDataRecords: [],
      sectionUiTransformedModel: null,
      sectionCardSummary: null,
      isDisplayEnabled: true
    }
  }
  return data
}

export function loadSectionRecordFromResponse(
  resp: GetCaseQuestionnaireSectionResponse,
  // eslint-disable-next-line
  sectionUiTransformedModel: any
): ActiveQuestionnaireSection {
  const newRecord = getEmptyActiveQuestionnaireSection()
  if (resp.cases.caseQuestionnaireSectionById) {
    const rec = resp.cases.caseQuestionnaireSectionById
    newRecord.isActiveSection = true
    if (newRecord.section) {
      newRecord.section.id = rec.id
      newRecord.section.sectionSystemName = rec.sectionSystemName
      newRecord.section.sectionName = rec.sectionName
      newRecord.section.sectionStatus = rec.sectionStatus
      newRecord.section.sectionDescription = rec.sectionDescription
      newRecord.section.sectionOrder = rec.sectionOrder
      newRecord.section.version = rec.version
      newRecord.section.comments = rec.comments
      newRecord.section.currentActiveQuestionId = rec.currentActiveQuestionId
      newRecord.section.isRejected = rec.isRejected
      newRecord.section.sectionUiTransformedModel = sectionUiTransformedModel
      newRecord.section.isDisplayEnabled = rec.isDisplayEnabled
      newRecord.section.sectionCardSummary = rec.sectionCardSummary
      rec.sectionDataRecords.forEach((dataRec) => {
        const newDataRec = getEmptySectionDataRecord()
        newDataRec.id = dataRec.id
        newDataRec.sectionId = dataRec.sectionId
        newDataRec.questionKey = dataRec.questionKey
        newDataRec.questionDataJsonText = dataRec.questionDataJsonText
        newDataRec.questionCompleteFlag = dataRec.questionCompleteFlag
        newRecord.section?.sectionDataRecords.push(newDataRec)
      })
    }
  }
  return newRecord
}

export function saveUiTransformedModelToSessionStorage(
  activeSection: ActiveQuestionnaireSection
): void {
  if (activeSection && activeSection.isActiveSection && activeSection.section) {
    saveSectionUiTransformedModelToSessionStorage(
      activeSection.section.sectionUiTransformedModel
    )
  }
}

const activeQuestionnaireSection = createSlice({
  name: 'activeSection',
  initialState: initialState,
  reducers: {
    setActiveSection(
      state,
      { payload }: PayloadAction<ActiveQuestionnaireSection>
    ) {
      saveActiveSectionToSessionStorage(payload)
      return payload
    },
    setActiveQuestionId(state, { payload }: PayloadAction<number>) {
      state.activeQuestionId = payload
    },
    setCurrentActiveQuestionId(
      state,
      { payload }: PayloadAction<number | null>
    ) {
      if (state.isActiveSection && state.section) {
        state.section.currentActiveQuestionId = payload
        saveSectionCurrentActiveQuestionId(payload)
      }
    },
    setCurrentActiveGroup(state, { payload }: PayloadAction<number | null>) {
      if (state.isActiveSection && state.section) {
        state.section.currentActiveGroup = payload
        saveSectionCurrentActiveGroup(payload)
      }
    },
    setSavedDataIntoTransformModel(
      state,
      // eslint-disable-next-line
      { payload }: PayloadAction<Record<string, any> | null>
    ) {
      const questions = state.section?.sectionUiTransformedModel.Questions || []
      const questionIndex = payload ? payload['index'] : 0

      questions[questionIndex].UnitForm.Data = payload?.data
    },
    setFormDataToCurrentQuestion(
      state,
      { payload }: PayloadAction<Record<string, unknown> | null>
    ) {
      if (state.isActiveSection && state.section) {
        const currentActiveQuestionId = state.section.currentActiveQuestionId
        for (const question of state.section.sectionUiTransformedModel
          .Questions) {
          if (question.QuestionId === currentActiveQuestionId) {
            question.UnitForm.Data = payload
            break
          }
        }
        saveSectionUiTransformedModelToSessionStorage(
          state.section.sectionUiTransformedModel
        )
      }
    },
    setFormDataToCurrentGroupQuestion(
      state,
      { payload }: PayloadAction<Record<string, unknown> | null>
    ) {
      if (state.isActiveSection && state.section) {
        const currentActiveQuestionId = state.section.currentActiveQuestionId
        const groups = state.section.sectionUiTransformedModel.Groups

        if (groups && groups.length) {
          for (const element of groups) {
            const questions = element.Questions
            for (const question of questions) {
              if (question.QuestionId === currentActiveQuestionId) {
                question.UnitForm.Data = payload
                break
              }
            }
          }
        }

        saveSectionUiTransformedModelToSessionStorage(
          state.section.sectionUiTransformedModel
        )
      }
    },

    setFormDataToSectionDataRecord(
      state,
      { payload }: PayloadAction<Record<string, unknown> | null>
    ) {
      if (state.isActiveSection && state.section) {
        const currentActiveQuestionId = state.section.currentActiveQuestionId
        let questionKey = ''
        for (const question of state.section.sectionUiTransformedModel
          .Questions) {
          if (question.QuestionId === currentActiveQuestionId) {
            questionKey = question.Value
            break
          }
        }
        const sectionDataRecord = state.section?.sectionDataRecords.find(
          (sectionDataRecord) => sectionDataRecord.questionKey === questionKey
        )
        if (sectionDataRecord != undefined) {
          sectionDataRecord.questionDataJsonText = JSON.stringify(payload) || ''
        }
      }
    },
    setQuestionCompleteFlagToCurrentQuestion(
      state,
      { payload }: { payload: boolean }
    ) {
      if (state.isActiveSection && state.section) {
        const currentActiveQuestionId = state.section.currentActiveQuestionId
        for (const question of state.section.sectionUiTransformedModel
          .Questions) {
          if (question.QuestionId === currentActiveQuestionId) {
            question.UnitForm.QuestionCompleteFlag = payload
            break
          }
        }
        saveSectionUiTransformedModelToSessionStorage(
          state.section.sectionUiTransformedModel
        )
      }
    },
    setQuestionCompleteFlagToCurrentGroupQuestion(
      state,
      { payload }: { payload: boolean }
    ) {
      if (state.isActiveSection && state.section) {
        const currentActiveQuestionId = state.section.currentActiveQuestionId
        const groups = state.section.sectionUiTransformedModel.Groups

        if (groups && groups.length) {
          for (const element of groups) {
            const questions = element.Questions
            for (const question of questions) {
              if (question.QuestionId === currentActiveQuestionId) {
                question.UnitForm.QuestionCompleteFlag = payload
                break
              }
            }
          }
        }
        saveSectionUiTransformedModelToSessionStorage(
          state.section.sectionUiTransformedModel
        )
      }
    },
    setQuestionsWithDataInTransformModel(
      state,
      { payload }: PayloadAction<Record<string, unknown> | null>
    ) {
      if (state.section?.sectionUiTransformedModel) {
        state.section.sectionUiTransformedModel.Questions = payload
        saveSectionUiTransformedModelToSessionStorage(
          state.section.sectionUiTransformedModel
        )
      }
    },
    setGroupsWithDataInTransformModel(
      state,
      { payload }: PayloadAction<Record<string, unknown> | null>
    ) {
      if (state.section?.sectionUiTransformedModel) {
        state.section.sectionUiTransformedModel.Groups = payload
        saveSectionUiTransformedModelToSessionStorage(
          state.section.sectionUiTransformedModel
        )
      }
    }
  }
})

export const {
  setActiveSection,
  setCurrentActiveQuestionId,
  setCurrentActiveGroup,
  setFormDataToCurrentQuestion,
  setSavedDataIntoTransformModel,
  setFormDataToSectionDataRecord,
  setQuestionCompleteFlagToCurrentQuestion,
  setFormDataToCurrentGroupQuestion,
  setQuestionCompleteFlagToCurrentGroupQuestion,
  setQuestionsWithDataInTransformModel,
  setGroupsWithDataInTransformModel
} = activeQuestionnaireSection.actions

export default activeQuestionnaireSection.reducer
